import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ExtendRentalConfigModal from "@app/components/extend-rental-config-modal";
import { addToast } from "@app/components/toast/toast.service";
import { TunnelModal } from "@app/components/wan-modal";
import QRCodeModal from "@app/components/wan-modal/qr-modal.component";
import {
  CURRENT_ACCOUNT,
  DEFAULT_PAGE,
  DEFAULT_TOTAL_PAGES,
  PathnameUrl,
  VPS_OS,
  VPS_Status,
} from "@app/constants";
import AccountService from "@app/services/http/account.service";
import ComputerConfigService from "@app/services/http/computer.config.service";
import LogService from "@app/services/http/log.service";
import OrderVpsService from "@app/services/http/order.vps.service";
import SshService from "@app/services/http/ssh.service";
import { openPortalDialog } from "@app/services/modal.service";
import { storeMyInfo } from "@app/store/my-info/my-info.action";
import { Account, ComputerConfig, CreateOrderVps, LogRequest, OrderVps, Tunnel } from "@app/types";
import { bySortOrder, downloadFile } from "@app/utils/util";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import { useNow, useSilentSync } from "@core/hooks/use-silent-sync.hook";
import StorageService from "@core/services/storage";
import { useT } from "@app/contexts/preferences.context";
import { IcServer } from "@app/components/icons";
import { btn, btnGhost } from "@app/components/ui";
import VpsCatalog from "./vps-catalog";
import VpsOrders, { VpsFilter, filterQuery, getOrderVpsEnd } from "./vps-orders";
import VpsSummary, { getVpsOsOptions } from "./vps-summary";

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

/**
 * Trang Thuê VPS: phần trên là thuê VPS (VpsRental + RentalConfigModal cũ),
 * phần dưới là VPS của tôi (OrderVpsManagement + ActionOrderVpsModal cũ).
 */
function VpsRental() {
  const t = useT();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [configs, setConfigs] = useState<ComputerConfig[]>([]);
  const [currentAccount, setCurrentAccount] = useState<Account>();

  const [selected, setSelected] = useState<ComputerConfig>();
  const [months, setMonths] = useState(1);
  const [os, setOs] = useState<VPS_OS | "">("");
  const [agree, setAgree] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [ordersVps, setOrdersVps] = useState<OrderVps[]>([]);
  const [count, setCount] = useState(0);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<VpsFilter>("");
  const [vpsName, setVpsName] = useState("");

  const [update, forceUpdate] = useForceUpdate();
  const { subscribeOnce, subscribeUntilDestroy } = useObservable();

  // Giờ hiện tại: VPS quá ngày kết thúc chuyển "Kết thúc" ngay (server đổi status bằng cron checkTimeOutOrderVps)
  const now = useNow(
    ordersVps.filter((order) => order.status === VPS_Status.ON_RENTAL).map((order) => getOrderVpsEnd(order).valueOf())
  );

  // Đồng bộ nền mỗi 60s khi còn VPS đang thuê / đang chờ admin cấp: im lặng, chỉ thay khi dữ liệu đổi
  useSilentSync(
    () =>
      OrderVpsService.getOrdersVps(page, "", "", "", ...filterQuery(filterStatus, search), true).subscribe(
        (res) => {
          if (JSON.stringify(res.data || []) !== JSON.stringify(ordersVps)) {
            setOrdersVps(res.data || []);
            setTotalPages(res.data?.length ? res.total_page : DEFAULT_TOTAL_PAGES);
            setCount(res.count || 0);
          }
        }
      ),
    ordersVps.some((order) => order.status === VPS_Status.ON_RENTAL)
  );

  const getMyInfo = () => {
    subscribeOnce(AccountService.getMyInfo(), (account) => {
      if (account) {
        setCurrentAccount(account);
        StorageService.setObject(CURRENT_ACCOUNT, account);
        dispatch(storeMyInfo(account));
      }
    });
  };

  useEffect(() => {
    getMyInfo();
    subscribeOnce(ComputerConfigService.getConfigs(), (configs) => {
      // cấu hình theo thứ tự admin nhập (API đã xếp, xếp lại cho chắc)
      setConfigs(configs?.length ? [...configs].sort(bySortOrder) : []);
    });
  }, []);

  // Tìm theo tên cấu hình, chờ 300ms như trang cũ
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(DEFAULT_PAGE);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    subscribeUntilDestroy(
      OrderVpsService.getOrdersVps(page, "", "", "", ...filterQuery(filterStatus, search)),
      (res) => {
        setOrdersLoaded(true);

        if (res.data.length) {
          setOrdersVps(res.data);
          setTotalPages(res.total_page);
          setCount(res.count);
        } else {
          setOrdersVps([]);
          setTotalPages(DEFAULT_TOTAL_PAGES);
          setCount(0);
        }
      }
    );
  }, [page, search, filterStatus, update]);

  /* ---------- thuê VPS (RentalConfigModal cũ) ---------- */
  const handlePick = (config?: ComputerConfig, nextOs?: VPS_OS) => {
    setSelected(config);
    setShowErrors(false);

    const options = getVpsOsOptions(config);

    setOs(nextOs && options.some((o) => o.value === nextOs) ? nextOs : options[0]?.value || "");
  };

  const handleCreate = () => {
    if (!selected) return;

    if (!os || !agree) {
      setShowErrors(true);

      return;
    }

    const body: CreateOrderVps = {
      rental_period: months,
      config_id: selected._id,
      os,
      name: vpsName.trim(),
    };

    setBusy(true);

    OrderVpsService.createOrderVps(body).subscribe({
      next: () => {
        addToast({ text: "Thuê VPS Thành Công" });
        setBusy(false);
        setAgree(false);
        setVpsName("");
        handlePick(undefined);
        getMyInfo();
        setFilterStatus("");
        setPage(DEFAULT_PAGE);
        forceUpdate();
        scrollTo("vps-orders");
      },
      error: () => setBusy(false),
    });
  };

  /** Tạo lại: chọn lại gói, số tháng và hệ điều hành của VPS đã kết thúc rồi để khách bấm Bắt đầu thuê */
  const handleRecreate = (order: OrderVps) => {
    const configId = (order.vps?.config as ComputerConfig)?._id || (order.vps?.config as string);
    const config = configs.find((c) => c._id === configId);

    if (!config) {
      addToast({ text: t("Gói này hiện không còn cung cấp"), status: "inValid" });

      return;
    }

    handlePick(config, order.vps?.os);
    setMonths(Math.min(12, order.rental_period || 1));
    setVpsName(order.name || "");
    scrollTo("vps-rent");
  };

  /* ---------- thao tác VPS (ActionOrderVpsModal cũ) ---------- */
  const handleExtendOrderVps = (order: OrderVps) => {
    const obs = openPortalDialog(ExtendRentalConfigModal, {
      price: (order.vps.config as ComputerConfig).monthly_rental_price,
      order,
    });

    obs.afterClosed().subscribe((data) => {
      // Thiếu tiền: sang trang Nạp tiền với số tiền còn thiếu
      if (data?.topup) {
        navigate(`${PathnameUrl.RECHARGE}?amount=${Math.ceil(data.topup)}&from=extend_vps`);

        return;
      }

      if (!data?.rental_period) {
        return;
      }

      subscribeUntilDestroy(OrderVpsService.extendOrderVps(order._id, data.rental_period), () => {
        addToast({ text: "Gia hạn thành công" });
        getMyInfo();
        forceUpdate();
      });
    });
  };

  const handleConnectVps = (order: OrderVps) => {
    OrderVpsService.getVpsRdpFile(order._id).subscribe({
      next: (data) => {
        const contentDisposition = data.responseHeaders["content-disposition"];

        const fileNameOriginal = contentDisposition?.split("filename=")?.[1];

        if (fileNameOriginal) {
          const fileName = fileNameOriginal.substring(1, fileNameOriginal.length - 2);

          if (fileName) {
            downloadFile(data.response, "application/rdp", fileName);
          }
        }
      },
      // lỗi đã được HttpService.requestDownload báo bằng toast (đọc message trong body)
      error: () => undefined,
    });
  };

  const handleChoosePcrenderTunnel = (order: OrderVps) => {
    const vps = order.vps;
    const createSshModalObs = openPortalDialog(TunnelModal);

    createSshModalObs.afterClosed().subscribe((data) => {
      if (data?.os) {
        const tunnelRequest: Tunnel = {
          allowIp: vps?.int_ip + "/32",
          os: data.os,
          api_tunnel: vps?.api_tunnel,
          vps_id: vps?._id,
        };

        // VPS mới: admin chọn SSH (ssh_tunnel); VPS cũ: api_tunnel nhập tay
        vps?.ssh_tunnel || vps?.api_tunnel
          ? subscribeOnce(SshService.chooseTunnelForVps(tunnelRequest), (data1) => {
              //mobile
              if (data.os === "mobile") {
                openPortalDialog(QRCodeModal, data1);

                return;
              }

              //pc
              const contentDisposition = data1.responseHeaders["content-disposition"];

              const fileNameOriginal = contentDisposition?.split("filename=")?.[1];

              if (fileNameOriginal) {
                const fileName = fileNameOriginal.substring(1, fileNameOriginal.length - 2);

                if (fileName) {
                  downloadFile(data1.response, "application/exe", fileName);

                  const logRequest: LogRequest = {
                    message: `Pcrender Tunnel - Tải thành công file ${fileName}`,
                  };

                  subscribeOnce(LogService.createLogByUser(logRequest), () => {});
                } else {
                  const logRequest: LogRequest = {
                    message: `Pcrender Tunnel - Tải file exe KHÔNG thành công ${fileName}`,
                  };

                  subscribeOnce(LogService.createLogByUser(logRequest), () => {});
                }
              }
            })
          : addToast({ text: "VPS này chưa có API Tunnel, hãy liên hệ Admin!", status: "warn" });
      }
    });
  };

  return (
    <div className="flex flex-col gap-6 px-6 pb-12 pt-6 max-[1100px]:pb-24 max-[900px]:px-4 max-[760px]:gap-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">{t("Thuê VPS")}</h1>
          <p className="m-0 mt-1 max-w-[62ch] text-muted">
            {t("Máy chủ ảo thuê theo tháng, có gói kèm GPU để chạy giả lập. Đặt xong, admin sẽ cấp VPS và thông tin đăng nhập.")}
          </p>
        </div>
        <button type="button" className={clsx(btn, btnGhost)} onClick={() => scrollTo("vps-orders")}>
          <IcServer className="h-[18px] w-[18px]" />
          {t("VPS của tôi")} ({count})
        </button>
      </div>

      <ol className="m-0 grid list-none grid-cols-3 gap-2.5 p-0 max-[760px]:grid-cols-1 max-[760px]:gap-1.5">
        {[
          ["Chọn gói VPS", "So sánh CPU, RAM, GPU và giá theo tháng."],
          ["Chọn số tháng và hệ điều hành", "Thanh toán từ số dư, không hoàn tiền sau khi thuê."],
          ["Chờ hệ thống cấp VPS", "Xong sẽ hiện tài khoản, mật khẩu, IP trong VPS của tôi."],
        ].map(([title, desc], i) => (
          <li
            key={title}
            className="flex items-start gap-2.5 rounded-card border border-line bg-surface px-3.5 py-2.5 text-[12.5px] leading-snug text-muted max-[760px]:items-center max-[760px]:px-3 max-[760px]:py-2"
          >
            <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-brand-soft text-xs font-bold text-brand">
              {i + 1}
            </span>
            <span>
              <b className="block text-[13px] font-semibold text-fg">{t(title)}</b>
              <span className="max-[760px]:hidden">{t(desc)}</span>
            </span>
          </li>
        ))}
      </ol>

      <div
        id="vps-rent"
        className="grid scroll-mt-20 grid-cols-[minmax(0,1fr)_310px] items-stretch gap-5 max-[1240px]:grid-cols-[minmax(0,1fr)_300px] max-[1100px]:grid-cols-1"
      >
        <VpsCatalog
          configs={configs}
          discount={currentAccount?.discount_vps || 0}
          selectedId={selected?._id || ""}
          onPick={(config) => handlePick(config)}
        />
        <VpsSummary
          config={selected}
          name={vpsName}
          onName={setVpsName}
          discount={currentAccount?.discount_vps || 0}
          balance={currentAccount?.balance || 0}
          months={months}
          os={os}
          agree={agree}
          showErrors={showErrors}
          busy={busy}
          onMonths={setMonths}
          onOs={setOs}
          onAgree={setAgree}
          onUnpick={() => handlePick(undefined)}
          onCreate={handleCreate}
          onTopup={(need) => navigate(`${PathnameUrl.RECHARGE}?amount=${Math.ceil(need)}&from=vps`)}
        />
      </div>

      <VpsOrders
        now={now}
        loading={!ordersLoaded}
        orders={ordersVps}
        count={count}
        page={page}
        totalPages={totalPages}
        status={filterStatus}
        search={searchInput}
        onSearch={setSearchInput}
        onStatus={(status) => {
          setFilterStatus(status);
          setPage(DEFAULT_PAGE);
        }}
        onPageChange={setPage}
        onRdp={handleConnectVps}
        onTunnel={handleChoosePcrenderTunnel}
        onExtend={handleExtendOrderVps}
        onRecreate={handleRecreate}
      />
    </div>
  );
}

export default VpsRental;
